import { useState, useEffect } from "react";
import { Row, Col, Modal, Button, message, Spin } from "antd";
import { withRouter } from "react-router";
import { LeftCol, RightCol } from "./components";
import {
  getCopilotChatById,
  updateCopilotChatById,
  uploadCopilotChatImage,
} from "@api/copilots/browser-extension/chat/copilotChat";
import { getAllCopilotChatCategories } from "@api/copilots/browser-extension/chat/copilotChatCategory";
import { SuccessNotification, ErrorNotification } from "../..";
import { getAllPlans } from "@api/subscription/plan";

const initialState = {
  image: "",
  name: "",
  icon: "📊",
  color: "#6366f1",
  shortDescription: "",
  command: "",
  description: "",
  category: null,
  tags: [],
  welcomeMessage: "",
  suggestedQuestions: [],
  order: 0,
  plans: [],
  model: "gpt-4o",
  temperature: 0.7,
  max_tokens: 2000,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  publish: false,
  featured: false,
};

const CopilotChatUpdateModal = ({
  open,
  handleModalClick,
  fetchCopilotChats,
  copilotChatId,
}) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);

  // Fetch all categories, plans and copilot data
  const fetchData = async () => {
    if (!copilotChatId) return;

    setLoading(true);
    try {
      const [planRes, categoryRes, copilotRes] = await Promise.all([
        getAllPlans(),
        getAllCopilotChatCategories(),
        getCopilotChatById(copilotChatId),
      ]);

      // API returns { plans: [...], pagination: {...} }
      setPlans(planRes.data.plans || planRes.data || []);
      setCategories(categoryRes.data || []);

      // Set copilot data with defaults for new fields
      const copilotData = copilotRes.data;
      setValues({
        ...initialState,
        ...copilotData,
        // Handle category object vs id
        category: copilotData.category?._id || copilotData.category || null,
        // Ensure arrays have defaults
        tags: copilotData.tags || [],
        suggestedQuestions: copilotData.suggestedQuestions || [],
        plans: copilotData.plans || [],
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      ErrorNotification("Error loading copilot data");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && copilotChatId) {
      fetchData();
    }
  }, [open, copilotChatId]);

  // Handle form change
  const handleFormChange = (e) => {
    e.preventDefault();
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle copilot update
  const handleCopilotUpdate = async (e) => {
    setLoading(true);
    e.preventDefault();

    // Validate form
    if (!values.name) {
      setLoading(false);
      return ErrorNotification("Please enter a name");
    }

    if (!values.command) {
      setLoading(false);
      return ErrorNotification("Please enter a system prompt");
    }

    // Filter out empty suggested questions
    const cleanedValues = {
      ...values,
      suggestedQuestions: values.suggestedQuestions?.filter((q) => q.trim()) || [],
    };

    // Update copilot chat
    updateCopilotChatById(copilotChatId, cleanedValues)
      .then(() => {
        setValues(initialState);
        handleModalClick();
        fetchCopilotChats();
        SuccessNotification("Copilot chat updated successfully");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Error updating copilot chat", err);
        ErrorNotification("Error updating copilot chat");
      });
  };

  // Before image upload
  const beforeImageUpload = (file) => {
    // Image can be jpeg, png, or gif
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle image upload
  const handleUploadImage = async (options) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    // Make request to upload image
    try {
      setLoading(true);
      const res = await uploadCopilotChatImage(fmData, config);
      setValues({
        ...values,
        image: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Update Copilot Chat
          </h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: "13px" }}>
            Edit the AI assistant configuration
          </p>
        </div>
      }
      open={open}
      onCancel={handleModalClick}
      width={1200}
      centered
      style={{ top: "20px" }}
      bodyStyle={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
        padding: "16px 24px",
      }}
      footer={[
        <Button key="back" onClick={handleModalClick}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleCopilotUpdate}
          loading={loading}
        >
          Update Copilot
        </Button>,
      ]}
    >
      {loading && !values.name ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={24}>
          <Col span={14}>
            <LeftCol
              loading={loading}
              values={values}
              setValues={setValues}
              handleFormChange={handleFormChange}
              plans={plans}
              categories={categories}
              handleUploadImage={handleUploadImage}
              beforeImageUpload={beforeImageUpload}
            />
          </Col>

          <Col span={10}>
            <RightCol
              loading={loading}
              values={values}
              setValues={setValues}
              handleFormChange={handleFormChange}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default withRouter(CopilotChatUpdateModal);
